import {
  datepickerToISOFormat,
  FilterQuery
} from '@yes.technology/react-toolkit'
import cloneDeep from 'lodash/cloneDeep'
import { AllowedLocales } from 'shared/utils/date/formatDate'
import { isValidISOString } from 'shared/utils/date/isValidISOString'
import {
  isSituationField,
  isSpecialRelationFieldByObjectclass
} from 'shared/utils/specialRelationFields/specialRelationFields'
import XRegExp from 'xregexp'
import { ArrayFieldValue, FieldValues, GroupedFieldValues } from '../../types'
import { makeDesFilter } from '../FilterLanguage'

const operatorsMapping = {
  gt: '$gt',
  gte: '$gte',
  lt: '$lt',
  lte: '$lte',
  in: '$in',
  nin: '$nin',
  eq: '$eq',
  ne: '$ne',
  REGEX: '$regex',
  filteraction: '$filteraction'
} as const

type OperatorMapping = typeof operatorsMapping

type Operator = keyof typeof operatorsMapping

type MongoOperator = OperatorMapping[keyof OperatorMapping]

type FilterValue = string | string[]

const allOperators = Object.keys(operatorsMapping) as Operator[]
const arrayOperators: Set<Operator> = new Set<Operator>(['in', 'nin'])
const stringOperators: Set<Operator> = new Set(
  allOperators.filter((op) => !arrayOperators.has(op))
)

type FilterCondition = {
  [key in MongoOperator]?: FilterValue
}

type FilterGroup = {
  [key: string]: FilterCondition
}

type ValidationResult = {
  success: boolean
  message?: string
}

type FilterItem = {
  uuid: string
  des: string
}

type SpecialRelationFieldValue = FilterItem & {
  uuid_Objectclass: string
  code: string
}

type ValidField =
  | { operator: 'in' | 'nin'; value: ArrayFieldValue }
  | { operator: Exclude<Operator, 'in' | 'nin'>; value: string }

function areOperatorAndValuesCompatibles(field: {
  operator: Operator
  value: string | ArrayFieldValue
}): field is ValidField {
  if (typeof field.value === 'string') {
    return stringOperators.has(field.operator)
  } else if (Array.isArray(field.value)) {
    return arrayOperators.has(field.operator)
  }
  return false
}

function makeIdsFilter(items: ArrayFieldValue): string[] {
  return items.map((i) => i.uuid)
}

function isSpecialRelationFieldValues(
  value: ArrayFieldValue
): value is SpecialRelationFieldValue[] {
  return (
    value.length > 0 &&
    typeof value[0].uuid_Objectclass === 'string' &&
    isSpecialRelationFieldByObjectclass(value[0].uuid_Objectclass)
  )
}

export function makeFilter(
  values: FieldValues,
  language?: string
): FilterQuery | false {
  const group: FilterGroup = {}

  const isValid = Object.keys(values).every((key) => {
    const raw = cloneDeep(values[key])

    if (key === 'groupType' || !raw.value || raw.value?.length === 0) {
      return true
    }

    if (!areOperatorAndValuesCompatibles(raw)) {
      return false
    }

    let filterContent: FilterValue
    let property: string

    if (raw.operator === 'filteraction') {
      property = 'uuid'
      filterContent = makeDesFilter(raw.operator, raw.value)
    } else if (typeof raw.value === 'string') {
      const isNonISODateTime =
        raw.type === 'DATETIME' && !isValidISOString(raw.value)

      const formattedValue = isNonISODateTime
        ? datepickerToISOFormat(raw.value, {
            locale: language as AllowedLocales,
            timezone: globalThis.waTimezone
          })
        : raw.value

      property = 'des'
      filterContent = makeDesFilter(raw.operator, formattedValue || '')
    } else if (isSpecialRelationFieldValues(raw.value)) {
      if (raw.value.length === 1) {
        const isSituation = isSituationField(key)
        if (
          isSituation &&
          raw.value[0].code === globalThis.activeSituationCode
        ) {
          raw.operator = 'gte'
        } else {
          raw.operator = raw.operator === 'in' ? 'eq' : 'ne'
        }
        raw.type = 'STRING'
        const singleCode = raw.value[0].code
        property = 'des'
        filterContent = makeDesFilter(raw.operator, singleCode)
      } else {
        const codes = raw.value.map((v) => v.code)
        property = 'des'
        filterContent = codes
      }
    } else {
      property = 'uuid'
      filterContent = makeIdsFilter(raw.value)
    }

    const filterType: FilterCondition = {}
    filterType[operatorsMapping[raw.operator]] = filterContent
    group[key + '.' + property] = filterType

    return true
  })

  if (!isValid) {
    return false
  }

  return {
    $and: [group]
  }
}

function splitComposition(composition: string) {
  return XRegExp.matchRecursive(composition, '\\(', '\\)', 'gi', {
    valueNames: ['unmatch', null, 'match', null]
  })
}

function validParentheses(composition: string): boolean {
  let depth = 0
  for (let i = 0; i < composition.length; i++) {
    if (composition[i] === '(') {
      depth++
    } else if (composition[i] === ')') {
      depth--
    }
    if (depth < 0) return false
  }
  if (depth > 0) return false
  return true
}

function validateLogicalStructure(composition: string): ValidationResult {
  const parts = splitComposition(composition)
  const len = parts.length

  if (len === 0) {
    if (
      !/^(\s*(([1-9]\d*)+[\s+]+[a-z]+[\s+]+([1-9]\d*)|[1-9]\d*)(\s+|$))+$/i.test(
        composition.trim()
      )
    ) {
      return {
        success: false,
        message: `Composição inválida em: ${composition}`
      }
    }
  } else if (len === 1) {
    return validateLogicalStructure(parts[0].value.trim())
  } else if (len === 2) {
    if (parts[1].name === 'unmatch') {
      if (!/^(\s*([a-z]+[\s+]+[1-9]\d*))$/i.test(parts[1].value.trim())) {
        return {
          success: false,
          message: `Composição inválida em: ${parts[1].value.trim()}`
        }
      }
      return validateLogicalStructure(parts[0].value.trim())
    }

    if (parts[0].name === 'unmatch' && parts[0].value !== '') {
      if (!/^(\s*(([1-9]\d*)+[\s+]+[a-z]+))$/i.test(parts[0].value.trim())) {
        return {
          success: false,
          message: `Composição inválida em: ${parts[0].value.trim()}`
        }
      }
      return validateLogicalStructure(parts[1].value.trim())
    }
  } else if (len > 2) {
    return {
      success: false,
      message: 'Permitido apenas 2 operadores por nível'
    }
  }

  return {
    success: true
  }
}

export function validateComposition(
  composition: string,
  totalGroup: number
): ValidationResult {
  if (composition === '') {
    return { success: false, message: 'Composição Filtro é obrigatório' }
  }

  if (!validParentheses(composition)) {
    return { success: false, message: 'Parenteses inválidos' }
  }

  const compositionWithoutParentheses = composition.replace(/[()]/g, '')

  if (!/^(\s*(OR|AND|\d+|\s+)(\s+|$))+$/i.test(compositionWithoutParentheses)) {
    return { success: false, message: 'Operador não permitido' }
  }

  const onlyNumbers = compositionWithoutParentheses
    .replace(/\s/g, ';')
    .replace(/[^0-9]/g, ';')
  const listGroups = onlyNumbers.split(';').filter((el) => el !== '')
  const listUniqueGroups = new Set(listGroups)

  if (listUniqueGroups.size !== listGroups.length) {
    return { success: false, message: 'Não é permitido repetir grupo' }
  }

  const doNotExist: number[] = []
  for (const group of Array.from(listUniqueGroups)) {
    if (Number(group) > totalGroup) {
      doNotExist.push(Number(group))
    }
  }
  if (doNotExist.length) {
    return {
      success: false,
      message: `Grupo${doNotExist.length > 1 ? 's' : ''} ${doNotExist.join(
        ', '
      )} não existe${doNotExist.length > 1 ? 'm' : ''}`
    }
  }

  return validateLogicalStructure(composition)
}

type InterpretFiltersCompositionParams = {
  groups: GroupedFieldValues
  composition: string
  language?: string
}

export function interpretFiltersComposition({
  groups,
  composition,
  language
}: InterpretFiltersCompositionParams): FilterQuery {
  let query: FilterQuery = {}

  const parts = splitComposition(composition)
  const len = parts.length

  if (len === 0) {
    const values = composition.trim().split(' ')

    if (values.length === 1) {
      const filterResult = makeFilter(groups[composition], language)
      if (filterResult) {
        query = filterResult
      }
    } else {
      const operator = `$${values[1].toLowerCase()}` as '$and' | '$or'
      const filter1 = makeFilter(groups[values[0]], language)
      const filter2 = makeFilter(groups[values[2]], language)

      if (filter1 && filter2) {
        query[operator] = [filter1, filter2]
      }
    }
  } else if (len === 1) {
    return interpretFiltersComposition({
      groups,
      composition: parts[0].value,
      language
    })
  } else if (len > 1) {
    if (parts[0].name === 'match') {
      const [operator, group] = parts[1].value.trim().split(' ')
      const mongoOperator = `$${operator.toLowerCase()}` as '$and' | '$or'
      const filter = makeFilter(groups[group], language)

      if (filter) {
        query[mongoOperator] = [filter]
        const recursiveResult = interpretFiltersComposition({
          groups,
          composition: parts[0].value,
          language
        })
        if (query[mongoOperator]) {
          query[mongoOperator].push(recursiveResult)
        }
      }
    }

    if (parts[0].name === 'unmatch') {
      const [group, operator] = parts[0].value.trim().split(' ')
      const mongoOperator = `$${operator.toLowerCase()}` as '$and' | '$or'
      const filter = makeFilter(groups[group], language)

      if (filter) {
        query[mongoOperator] = [filter]
        const recursiveResult = interpretFiltersComposition({
          groups,
          composition: parts[1].value,
          language
        })
        if (query[mongoOperator]) {
          query[mongoOperator].push(recursiveResult)
        }
      }
    }
  }

  return query
}

type ToYesFilterParams = {
  groups: GroupedFieldValues
  composition: string
  language?: string
}

export function toYesFilter({
  groups,
  composition,
  language
}: ToYesFilterParams): FilterQuery {
  const { success } = validateComposition(
    composition,
    Object.keys(groups).length
  )
  if (!success) {
    return {}
  }
  return interpretFiltersComposition({ groups, composition, language })
}
